export type CalculatorSeoContent = Readonly<{
  summary: string;
  formula: string;
  usage: readonly string[];
  note?: string;
}>;

export const calculatorSeoContent: Readonly<Record<string, CalculatorSeoContent>> = {
  margin: {
    summary: '판매가와 상품 원가뿐 아니라 플랫폼·결제 수수료, 배송비, 포장비, 주문당 광고비와 기타 비용까지 반영해 주문 1건의 예상 이익과 마진율을 계산합니다. 목표 마진별 최소 판매가와 할인·원가 상승 시나리오도 함께 비교할 수 있습니다.',
    formula: '입력비용 반영 마진율 = (판매가 - 원가 - 수수료 - 배송·포장·광고·기타비용) ÷ 판매가 × 100',
    usage: ['오픈마켓·자사몰 상품의 실제 주문당 수익성을 점검할 때', '할인 전후 또는 원가 인상 전후의 마진 변화를 비교할 때', '수수료와 주문당 비용을 반영한 목표 판매가를 검토할 때'],
    note: '입력한 주문당 비용을 기준으로 한 의사결정용 추정치입니다. 인건비·임대료 같은 고정비, 세금, 환불·반품, 재고 손실 등 입력하지 않은 비용은 별도로 고려해야 합니다.',
  },
  'target-margin-price': {
    summary: '원가와 원하는 목표 마진율을 기준으로 필요한 판매가를 역산합니다. 목표 이익률을 먼저 정한 뒤 가격을 결정해야 할 때 사용할 수 있습니다.',
    formula: '필요 판매가 = 원가 ÷ (1 - 목표 마진율)',
    usage: ['신상품의 최초 판매가를 정할 때', '원가 인상 후 목표 마진을 유지할 새 가격을 계산할 때', '도매·소매 가격 정책을 비교할 때'],
  },
  vat: {
    summary: '공급가액에 10% 부가가치세를 더한 합계금액을 계산하거나, 부가세가 포함된 금액에서 공급가액과 부가세를 분리합니다.',
    formula: '부가세 = 공급가액 × 10%, 부가세 포함 금액의 공급가액 = 합계금액 ÷ 1.1',
    usage: ['견적서의 공급가액과 부가세를 확인할 때', '부가세 포함 가격을 공급가액 기준으로 나눌 때', '거래 전 합계금액을 빠르게 확인할 때'],
    note: '일반적인 10% 부가가치세 계산을 위한 기초 도구입니다. 영세율·면세 등 개별 거래의 세무 처리는 별도로 확인해야 합니다.',
  },
  'break-even': {
    summary: '고정비와 판매단가·단위당 변동비로 손익분기 판매량과 매출을 계산하고, 목표이익을 달성하기 위해 필요한 판매량·매출까지 역산합니다. 현재 판매량을 입력하면 예상 손익과 손익분기점 대비 여유를 확인하고, 판매단가 인하·변동비 상승·고정비 상승 시나리오도 비교할 수 있습니다.',
    formula: '손익분기 판매량 = 고정비 ÷ 단위당 공헌이익, 목표이익 판매량 = (고정비 + 목표이익) ÷ 단위당 공헌이익',
    usage: ['월 고정비를 회수하기 위한 최소 판매량과 매출을 정할 때', '목표이익을 달성하기 위해 필요한 판매량을 역산할 때', '현재 판매량이 손익분기점을 얼마나 넘었는지 확인할 때', '가격 인하나 원가·고정비 상승이 BEP에 미치는 영향을 비교할 때'],
    note: '입력한 고정비와 단위당 변동비를 기준으로 한 단순 손익 모델입니다. 세금, 환불·반품, 재고 손실, 판매량에 따라 달라지는 수수료 등은 별도로 고려해야 합니다.',
  },
  roas: {
    summary: '광고비와 광고 매출로 현재 ROAS를 계산하고, 광고비 제외 마진율을 입력하면 손익분기 ROAS와 광고비 차감 후 추정 손익을 확인할 수 있습니다. 목표 ROAS를 넣어 필요한 광고 매출과 허용 광고비를 역산하고, 광고 매출 감소·광고비 상승 시나리오도 비교합니다.',
    formula: 'ROAS = 광고 매출 ÷ 광고비 × 100, 손익분기 ROAS = 1 ÷ 광고비 제외 마진율(decimal) × 100',
    usage: ['캠페인별 현재 ROAS와 매출 대비 광고비 비중을 확인할 때', '상품 마진을 반영해 실제 광고 손익분기 ROAS를 판단할 때', '목표 ROAS에서 필요한 매출 또는 허용 가능한 광고비 상한을 역산할 때', '매출 하락·광고비 상승 시에도 광고 손익이 유지되는지 점검할 때'],
    note: '광고 매출은 사용자가 입력한 귀속 매출을 그대로 사용하며 광고 기여도를 별도로 검증하지 않습니다. 광고비 제외 마진율에는 상품원가·수수료·배송비 등 광고비 이전에 차감할 비용을 반영해야 하며, 세금·환불·반품·LTV·재구매는 자동으로 추정하지 않습니다.',
  },
  'supply-price-reverse': {
    summary: '부가세가 이미 포함된 합계금액에서 공급가액과 10% 부가세를 역산합니다. 총액만 알고 있을 때 세금계산서 기준 금액을 빠르게 나누는 데 적합합니다.',
    formula: '공급가액 = 부가세 포함 합계금액 ÷ 1.1',
    usage: ['총 결제금액에서 공급가액을 확인할 때', '부가세 포함 견적을 공급가액과 세액으로 나눌 때', '매출 자료의 VAT 포함 금액을 기초적으로 분리할 때'],
    note: '일반적인 10% 부가가치세 관계를 이용한 역산입니다. 면세·영세율 거래에는 적용하지 않습니다.',
  },
  'withholding-33': {
    summary: '3.3% 원천징수 전 금액에서 예상 실수령액을 계산하거나, 원하는 실수령액을 기준으로 세전 금액을 역산합니다.',
    formula: '실수령액 = 세전 금액 × 0.967, 세전 금액 = 실수령액 ÷ 0.967',
    usage: ['프리랜서·인적용역 대금의 예상 실수령액을 확인할 때', '실수령액 기준으로 계약 금액을 역산할 때', '3.3% 공제 전후 금액을 빠르게 비교할 때'],
    note: '3.3%를 단순 적용한 계산값입니다. 실제 원천징수 대상 여부와 최종 세액은 소득 형태와 신고 상황에 따라 달라질 수 있습니다.',
  },
  'discount-margin': {
    summary: '정상 판매가에 할인을 적용한 뒤 실제 판매가, 할인 금액, 이익과 마진율을 계산합니다. 할인 행사가 수익성에 미치는 영향을 바로 확인할 수 있습니다.',
    formula: '할인 판매가 = 정상 판매가 × (1 - 할인율), 할인 후 마진율 = (할인 판매가 - 원가) ÷ 할인 판매가 × 100',
    usage: ['세일 할인율을 정하기 전에 남는 이익을 확인할 때', '쿠폰·프로모션 적용 후 마진을 비교할 때', '원가 이하 판매 위험을 점검할 때'],
  },
  'target-profit-revenue': {
    summary: '고정비와 변동비율, 목표이익을 기준으로 목표를 달성하기 위해 필요한 매출을 계산합니다. 단순 손익분기점을 넘어 이익 목표를 매출 목표로 바꿀 때 사용합니다.',
    formula: '필요 매출 = (고정비 + 목표이익) ÷ 공헌이익률',
    usage: ['월 목표이익을 매출 목표로 환산할 때', '고정비 증가 후 필요한 매출 수준을 다시 계산할 때', '변동비율 개선이 목표 매출에 미치는 영향을 비교할 때'],
  },
  'break-even-roas': {
    summary: '마진율을 기준으로 광고비를 회수하기 위해 필요한 최소 ROAS를 계산합니다. 실제 ROAS가 이 기준보다 낮으면 광고 매출이 발생해도 마진 기준으로 손실일 수 있습니다.',
    formula: '손익분기 ROAS = 1 ÷ 마진율(decimal) × 100',
    usage: ['상품 마진율별 최소 광고 성과 기준을 정할 때', '캠페인 중단·증액 기준을 잡을 때', '매출 대비 허용 가능한 최대 광고비 비중을 확인할 때'],
  },
};

export function getCalculatorSeoContent(slug: string): CalculatorSeoContent | undefined {
  return calculatorSeoContent[slug];
}
